import { Component, computed, Inject, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faClose, faFile, faRotate, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { Subscription } from 'rxjs';

import { Media } from '@eiq/files/models';

import { IBlockForm } from '../../../../models/blocks/block-form.interface';
import { LoopMediaCatalogue } from '../../../../models/blocks/loop-media-catalogue.model';
import { LoopStimulusMedia } from '../../../../models/blocks/loop-stimulus-media.interface';
import { LoopStimulusRow } from '../../../../models/blocks/loop-stimulus-row.interface';
import { LOOP_STIMULUS_TYPES } from '../../../../models/blocks/loop-stimulus-types.constants';
import { SurveyEditorService } from '../../../../services/survey-editor.service';
import { StimulusPickerDialogComponent } from '../../../stimulus/stimulus-picker/stimulus-picker-dialog.component';

@Component({
  selector: 'empathy-loop-stimuli-dialog',
  templateUrl: 'loop-stimuli-dialog.component.html',
  styleUrls: ['./loop-stimuli-dialog.component.scss']
})
export class LoopStimuliDialogComponent implements OnInit, OnDestroy {
  readonly media$ = signal<LoopStimulusMedia[]>([]);
  readonly search$ = signal('');
  readonly selectedIds$ = signal(new Set<string>());
  readonly filtered$ = computed(() => {
    const search = this.search$().trim().toLowerCase();
    return this.media$().filter((media) =>
      [media.mediaId, String(media.sourceIndex).padStart(3, '0'), media.name, media.originalFilename ?? ''].some((value) =>
        value.toLowerCase().includes(search)
      )
    );
  });
  // The shared renderer refreshes URLs in place; keep saved catalogue metadata untouched.
  readonly rows$ = computed<LoopStimulusRow[]>(() =>
    this.filtered$().map((media) => ({
      media,
      originalFilename: media.originalFilename,
      nameControl: this.nameControls.get(media.mediaId),
      preview: this.previews.get(media.mediaId),
      selected: this.selectedIds$().has(media.mediaId)
    }))
  );
  readonly allSelected$ = computed(() => this.rows$().length > 0 && this.rows$().every((row) => row.selected));
  readonly someSelected$ = computed(() => this.rows$().some((row) => row.selected) && !this.allSelected$());
  readonly searchControl = new FormControl('');
  readonly columns = ['selection', 'id', 'preview', 'name', 'originalFile', 'actions'];

  readonly faClose = faClose;
  readonly faFile = faFile;
  readonly faRotate = faRotate;
  readonly faTrash = faTrash;

  private readonly dialog = inject(MatDialog);
  private readonly editor = inject(SurveyEditorService);
  private readonly catalogue: LoopMediaCatalogue;
  private readonly subscription = new Subscription();
  private readonly nameControls = new Map<string, FormControl<string>>();
  private readonly previews = new Map<string, LoopStimulusMedia[]>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly block: FormGroup<IBlockForm>,
    private readonly dialogRef: MatDialogRef<LoopStimuliDialogComponent>
  ) {
    this.catalogue = new LoopMediaCatalogue(block);
  }

  ngOnInit(): void {
    this.refreshMedia();
    this.subscription.add(this.block.controls.sourceMedia.valueChanges.subscribe(() => this.refreshMedia()));
    this.subscription.add(this.searchControl.valueChanges.subscribe((search) => this.onSearch(search ?? '')));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  trackRow(_index: number, row: LoopStimulusRow): string {
    return row.media.mediaId;
  }

  onSearch(search: string): void {
    this.search$.set(search);
  }

  onSelect(mediaId: string, selected: boolean): void {
    const ids = new Set(this.selectedIds$());
    if (selected) {
      ids.add(mediaId);
    } else {
      ids.delete(mediaId);
    }
    this.selectedIds$.set(ids);
  }

  onSelectAll(selected: boolean): void {
    const ids = new Set(this.selectedIds$());
    for (const row of this.rows$()) {
      if (selected) {
        ids.add(row.media.mediaId);
      } else {
        ids.delete(row.media.mediaId);
      }
    }
    this.selectedIds$.set(ids);
  }

  onRename(row: LoopStimulusRow): void {
    row.nameControl.markAsTouched();
    if (row.nameControl.invalid) {
      return;
    }
    const name = row.nameControl.value.trim();
    row.nameControl.setValue(name, { emitEvent: false });
    if (name !== row.media.name) {
      this.catalogue.rename(row.media.mediaId, name);
      this.refreshEditor();
    }
  }

  onRemove(mediaId: string): void {
    this.catalogue.remove(new Set([mediaId]));
    this.refreshEditor();
  }

  onRemoveSelected(): void {
    this.catalogue.remove(this.selectedIds$());
    this.refreshEditor();
  }

  onReplace(mediaId: string): void {
    this.subscription.add(
      this.dialog
        .open(StimulusPickerDialogComponent, { data: { multipleInd: false, types: LOOP_STIMULUS_TYPES } })
        .afterClosed()
        .subscribe((media: Media[] | undefined) => {
          if (media?.length !== 1) {
            return;
          }
          this.catalogue.replace(mediaId, media[0]);
          this.refreshEditor();
        })
    );
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private refreshEditor(): void {
    this.editor.surveyEditor$().blocks.refreshLists();
  }

  private refreshMedia(): void {
    const media = this.block.controls.sourceMedia.value ?? [];
    const retainedIds = new Set(media.map((entry) => entry.mediaId));
    for (const id of this.nameControls.keys()) {
      if (!retainedIds.has(id)) {
        this.nameControls.delete(id);
        this.previews.delete(id);
      }
    }
    for (const entry of media) {
      const preview = this.previews.get(entry.mediaId);
      if (preview) {
        preview[0].name = entry.name;
      } else {
        this.previews.set(entry.mediaId, [{ ...entry }]);
      }
      if (!this.nameControls.has(entry.mediaId)) {
        this.nameControls.set(
          entry.mediaId,
          new FormControl(entry.name, {
            nonNullable: true,
            validators: [Validators.required, Validators.pattern(/\S/)]
          })
        );
      }
    }
    this.media$.set(media);
    this.selectedIds$.set(new Set([...this.selectedIds$()].filter((id) => retainedIds.has(id))));
  }
}
